// src/panels/PanelUpgrade.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Radio,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';

import StripePaymentWrapper from '../components/StripePaymentWrapper';
import UpgradeSuccess from '../components/UpgradeSuccess';
import api from '../services/api';
import useToast from '../hooks/useToast';

function createRandomKey(prefix = 'checkout') {
  const random =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

  return `${prefix}_${random}`;
}

function formatCurrency(cents = 0, currency = 'usd') {
  const numericCents = Number(cents);
  const safeCents = Number.isFinite(numericCents) ? numericCents : 0;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'usd',
  }).format(safeCents / 100);
}

function entitlementsToFeatures(entitlements = {}) {
  const featureMap = [
    ['photo_pool', 'shared photos — school or personal'],
    ['personal_photos', 'personal photos'],
    ['school_photos', 'school photo picks'],
    ['memories', 'memories'],
    ['letter_characters', 'letter characters'],
    ['letter_count', 'personal notes'],
    ['personal_notes', 'personal notes'],
  ];

  return featureMap
    .filter(([key]) => entitlements[key] !== undefined && entitlements[key] !== null)
    .map(([key, label]) => `+${Number(entitlements[key]).toLocaleString()} ${label}`);
}

function pluralizePhoto(count) {
  return Number(count) === 1 ? 'photo' : 'photos';
}

function buildUpgradeContextNotice(upgradeContext, selectedOffer) {
  if (!upgradeContext?.reason) return null;

  if (upgradeContext.reason === 'memory_limit') {
    const currentLimit = Number(upgradeContext.currentLimit);
    const addedMemories = Number(selectedOffer?.entitlements?.memories);

    const hasCurrentLimit = Number.isFinite(currentLimit) && currentLimit > 0;
    const hasAddedMemories = Number.isFinite(addedMemories) && addedMemories > 0;

    return {
      title: 'You have reached your memory limit.',
      body: hasAddedMemories
        ? `Add ${addedMemories.toLocaleString()} more memories to this yearbook.`
        : hasCurrentLimit
          ? `Your current plan includes ${currentLimit.toLocaleString()} memories.`
          : 'Upgrade to add more memories to the yearbook.',
    };
  }

  if (upgradeContext.reason === 'letter_count_limit') {
    const currentLimit = Number(upgradeContext.currentLimit);
    const addedLetterCount = Number(
      selectedOffer?.entitlements?.letter_count ??
      selectedOffer?.entitlements?.personal_notes ??
      selectedOffer?.entitlements?.letters
    );

    const hasCurrentLimit = Number.isFinite(currentLimit) && currentLimit > 0;
    const hasAddedLetterCount = Number.isFinite(addedLetterCount) && addedLetterCount > 0;

    return {
      title: 'You have reached your personal note limit.',
      body: hasAddedLetterCount
        ? `Add ${addedLetterCount.toLocaleString()} more personal note${addedLetterCount === 1 ? '' : 's'} to this yearbook.`
        : hasCurrentLimit
          ? `Your current plan includes ${currentLimit.toLocaleString()} personal note${currentLimit === 1 ? '' : 's'}.`
          : 'Upgrade to add more personal notes to the yearbook.',
    };
  }

  if (upgradeContext.reason === 'letter_limit') {
    const currentLimit = Number(upgradeContext.currentLimit);
    const addedLetterLimit = Number(selectedOffer?.entitlements?.letter_characters);

    const hasCurrentLimit = Number.isFinite(currentLimit) && currentLimit > 0;
    const hasAddedLetterLimit = Number.isFinite(addedLetterLimit) && addedLetterLimit > 0;

    return {
      title: 'This note has reached the character limit.',
      body: hasAddedLetterLimit
        ? `Add ${addedLetterLimit.toLocaleString()} more characters for personal notes.`
        : hasCurrentLimit
          ? `Your current plan allows ${currentLimit.toLocaleString()} characters per note.`
          : 'Upgrade to write longer personal notes for the yearbook.',
    };
  }

  if (upgradeContext.reason === 'school_photo_limit') {
      const currentLimit = Number(upgradeContext.currentLimit);
      const usedSlots = Number(upgradeContext.usedSlots);
      const addedSchoolPhotos = Number(
        selectedOffer?.entitlements?.photo_pool ??
        selectedOffer?.entitlements?.school_photos
      );

      const hasCurrentLimit =
        Number.isFinite(currentLimit) && currentLimit >= 0;

      const hasUsedSlots =
        Number.isFinite(usedSlots) && usedSlots >= 0;

      const hasAddedSchoolPhotos =
        Number.isFinite(addedSchoolPhotos) &&
        addedSchoolPhotos > 0;

      let title = 'You have reached your school photo limit.';

      if (hasCurrentLimit && hasUsedSlots) {
        title =
          `You have selected ${usedSlots.toLocaleString()} of ` +
          `${currentLimit.toLocaleString()} school photos.`;
      }

      let body =
        'Upgrade to select more school photos for this yearbook.';

      if (hasAddedSchoolPhotos) {
        body =
          `This upgrade adds ${addedSchoolPhotos.toLocaleString()} ` +
          `more photos total — school or personal.`;
      } else if (hasCurrentLimit) {
        body =
          `Your current plan includes ` +
          `${currentLimit.toLocaleString()} school photos.`;
      }

      return { title, body };
    }

  if (upgradeContext.reason !== 'photo_limit') return null;

  const attempted = Number(upgradeContext.attempted);
  const remaining = Number(upgradeContext.remaining);
  const currentLimit = Number(upgradeContext.currentLimit);
  const addedPhotoLimit = Number(
    selectedOffer?.entitlements?.photo_pool ??
    selectedOffer?.entitlements?.personal_photos
  );

  const hasAttempted = Number.isFinite(attempted) && attempted > 0;
  const hasRemaining = Number.isFinite(remaining);
  const hasCurrentLimit = Number.isFinite(currentLimit) && currentLimit > 0;
  const hasAddedPhotoLimit = Number.isFinite(addedPhotoLimit) && addedPhotoLimit > 0;

  let title = 'You selected more photos than your current plan allows.';

  if (hasAttempted && hasRemaining && remaining > 0) {
    title = `You selected ${attempted} ${pluralizePhoto(attempted)}, but only have space for ${remaining} more ${pluralizePhoto(remaining)}.`;
  } else if (hasRemaining && remaining <= 0) {
    title = 'You have reached your personal photo limit.';
  }

  let body = 'Add more personal photos to the yearbook.';

  if (hasAddedPhotoLimit) {
    body = `Add ${addedPhotoLimit.toLocaleString()} more photos total — school or personal.`;
  } else if (hasCurrentLimit) {
    body = `Your current plan includes ${currentLimit.toLocaleString()} personal ${pluralizePhoto(currentLimit)}.`;
  }

  return { title, body };
}

function offerMatchesUpgradeContext(rawOffer, upgradeContext) {
  if (!upgradeContext?.reason) return true;

  const entitlements = rawOffer?.entitlements || {};

  const photoPool = Number(
    entitlements.photo_pool || 0
  );

  if (upgradeContext.reason === 'school_photo_limit') {
    return (
      photoPool > 0 ||
      Number(entitlements.school_photos || 0) > 0
    );
  }

  if (upgradeContext.reason === 'photo_limit') {
    return (
      photoPool > 0 ||
      Number(entitlements.personal_photos || 0) > 0
    );
  }

  if (upgradeContext.reason === 'memory_limit') {
    return Number(entitlements.memories || 0) > 0;
  }

  if (
    upgradeContext.reason === 'letter_count_limit' ||
    upgradeContext.reason === 'letter_limit'
  ) {
    return (
      Number(entitlements.personal_notes || 0) > 0 ||
      Number(entitlements.letter_count || 0) > 0 ||
      Number(entitlements.letter_characters || 0) > 0
    );
  }

  return true;
}

function normalizeOffer(rawOffer) {
  if (!rawOffer) return null;

  const id = rawOffer.offer_id || rawOffer.id || rawOffer.key || rawOffer.upgrade_key;
  if (!id) return null;

  const rawPriceCents = rawOffer.price_cents ?? rawOffer.amount_cents ?? rawOffer.priceCents;
  const priceCents = Number.isFinite(Number(rawPriceCents))
    ? Number(rawPriceCents)
    : Math.round(Number(rawOffer.price || 0) * 100);

  const entitlements = rawOffer.entitlements || {};
  const features = Array.isArray(rawOffer.features) && rawOffer.features.length > 0
    ? rawOffer.features
    : entitlementsToFeatures(entitlements);

  return {
    id,
    offer_id: rawOffer.offer_id || id,
    title: rawOffer.title || rawOffer.name || 'Upgrade yearbook',
    subtitle: rawOffer.subtitle || rawOffer.description || 'Add more room for photos, memories, and personal notes.',
    priceCents,
    currency: rawOffer.currency || 'usd',
    badge: rawOffer.badge || null,
    features,
    entitlements,
    ctaLabel: rawOffer.cta_label || rawOffer.ctaLabel || null,
  };
}

export default function PanelUpgrade({
  projectId,
  offers = [],
  childName,
  upgradeContext = null,
  onUpgradeConfirmed,
  onComplete,
  onCancel,
}) {
  const toast = useToast();

  const offerOptions = useMemo(
    () =>
      (Array.isArray(offers) ? offers : [])
        .filter((offer) =>
          offerMatchesUpgradeContext(offer, upgradeContext)
        )
        .map(normalizeOffer)
        .filter(Boolean),
    [offers, upgradeContext]
  );

  const [step, setStep] = useState('select');
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [completedOffer, setCompletedOffer] = useState(null);
  const [error, setError] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => createRandomKey());

  useEffect(() => {
    setIdempotencyKey(createRandomKey());
    setPaymentIntent(null);
    setError(null);
  }, [projectId, selectedOfferId]);

  useEffect(() => {
    if (!selectedOfferId && offerOptions.length > 0) {
      setSelectedOfferId(offerOptions[0].id);
    }
  }, [offerOptions, selectedOfferId]);

  const selectedOffer = offerOptions.find((offer) => offer.id === selectedOfferId) || offerOptions[0] || null;
  const paymentAmountCents = paymentIntent?.amount_cents ?? selectedOffer?.priceCents ?? 0;
  const paymentCurrency = paymentIntent?.currency || selectedOffer?.currency || 'usd';

  const startCheckout = async () => {
    if (!projectId || !selectedOffer?.id || isStartingCheckout) return;

    setIsStartingCheckout(true);
    setError(null);

    try {
      const intent = await api.createUpgradePaymentIntent(
        projectId,
        selectedOffer,
        {
          idempotencyKey,
          checkoutMode: 'upgrade',
        }
      );

      if (!intent?.client_secret) {
        throw new Error('Invalid payment intent');
      }

      setPaymentIntent(intent);
      setStep('payment');
    } catch (err) {
      const message = err.message || 'Could not start upgrade checkout.';
      setError(message);
      toast.error(message);
    } finally {
      setIsStartingCheckout(false);
    }
  };

  const handlePaymentComplete = async (paymentIntentId) => {
    if (isConfirming) return;

    setIsConfirming(true);
    setError(null);

    try {
      await api.finalizeStripePayment(paymentIntentId);

      setCompletedOffer({
        ...selectedOffer,
        chargedAmountCents: paymentAmountCents,
        chargedCurrency: paymentCurrency,
      });

      if (onUpgradeConfirmed) {
        const refreshResult = await onUpgradeConfirmed();

        if (refreshResult && refreshResult.ok === false) {
          throw refreshResult.error || new Error('Payment succeeded, but dashboard did not refresh.');
        }
      }

      setStep('success');
    } catch (err) {
      const message =
        err.message ||
        'Payment succeeded, but we could not update your yearbook yet. Please refresh.';

      setError(message);
      toast.error(message);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDone = useCallback(() => {
    if (onComplete) onComplete();
  }, [onComplete]);

  if (step === 'success') {
    return <UpgradeSuccess offer={completedOffer || selectedOffer} onDone={handleDone} />;
  }

  if (step === 'payment') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto' }}>
        <Button
          onClick={() => setStep('select')}
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          disabled={isConfirming}
          sx={{ mb: 2, color: 'text.secondary', pl: 0 }}
        >
          Back
        </Button>

        <Typography variant="h1" sx={{ mb: 0.75, textAlign: 'center' }}>
          Complete upgrade
        </Typography>
        <Typography sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
          Secure one-time payment for {selectedOffer?.title || 'your upgrade'}.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ p: 2.5, borderRadius: 4, bgcolor: 'grey.50', border: 1, borderColor: 'divider', mb: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{selectedOffer?.title}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                {selectedOffer?.subtitle}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, whiteSpace: 'nowrap' }}>
              {formatCurrency(paymentAmountCents, paymentCurrency)}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Total today</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
              {formatCurrency(paymentAmountCents, paymentCurrency)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, borderRadius: 4, border: 1, borderColor: 'divider' }}>
          <StripePaymentWrapper
            clientSecret={paymentIntent?.client_secret}
            amountCents={paymentAmountCents}
            currency={paymentCurrency}
            onComplete={handlePaymentComplete}
            processing={isConfirming}
          />
        </Box>
      </Box>
    );
  }

  if (offerOptions.length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center', py: 3 }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          No upgrade available
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>
          This yearbook does not currently have an available upgrade offer.
        </Typography>
        {onCancel && (
          <Button variant="contained" onClick={onCancel}>
            Back to yearbook
          </Button>
        )}
      </Box>
    );
  }

  const hasMultipleOffers = offerOptions.length > 1;
  const ctaText =
    selectedOffer?.ctaLabel ||
    `Continue — ${formatCurrency(
      selectedOffer?.priceCents,
      selectedOffer?.currency
    )} per copy`;
  const contextNotice = buildUpgradeContextNotice(upgradeContext, selectedOffer);

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h1" sx={{ mb: 0.75 }}>
          Upgrade {childName ? `${childName}'s` : 'your'} yearbook
        </Typography>
        <Typography sx={{ fontSize: 15, color: 'text.secondary', maxWidth: 440, mx: 'auto', lineHeight: 1.5 }}>
          Add more room for photos, memories, and the details that make the book feel personal.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {contextNotice && (
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: '#FFE082',
            bgcolor: '#FFF8E1',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'grey.900', mb: 0.5 }}>
            {contextNotice.title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
            {contextNotice.body}
          </Typography>
        </Box>
      )}

      {hasMultipleOffers ? (
        <Box sx={{ display: 'grid', gap: 1.25, mb: 2.5 }}>
          {offerOptions.map((offer) => {
            const isSelected = offer.id === selectedOffer?.id;
            return (
              <Box
                key={offer.id}
                onClick={() => setSelectedOfferId(offer.id)}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1.5px solid',
                  borderColor: isSelected ? 'text.primary' : 'divider',
                  bgcolor: isSelected ? 'grey.50' : 'background.paper',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 1.25,
                }}
              >
                <Radio checked={isSelected} sx={{ p: 0.25 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{offer.title}</Typography>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {formatCurrency(offer.priceCents, offer.currency)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        per printed copy
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                    {offer.subtitle}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 4,
            border: '1.5px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            mb: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography sx={{ fontSize: 21, fontWeight: 800 }}>{selectedOffer.title}</Typography>
                {selectedOffer.badge && <Chip label={selectedOffer.badge} size="small" sx={{ fontWeight: 700 }} />}
              </Box>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.45 }}>
                {selectedOffer.subtitle}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 25, fontWeight: 800 }}>
                {formatCurrency(selectedOffer.priceCents, selectedOffer.currency)}
              </Typography>

              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                per printed copy
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gap: 1 }}>
            {selectedOffer.features.map((feature, index) => (
              <Box key={`${feature}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <CheckIcon sx={{ fontSize: 18, mt: '1px', color: 'success.main' }} />
                <Typography sx={{ fontSize: 14, color: 'grey.800' }}>{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={startCheckout}
        sx={{ py: 1.7, fontSize: 16 }}
        disabled={isStartingCheckout || !selectedOffer}
      >
        {isStartingCheckout ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} color="inherit" />
            Starting checkout...
          </Box>
        ) : (
          ctaText
        )}
      </Button>

      {onCancel && (
        <Button fullWidth onClick={onCancel} sx={{ mt: 1, color: 'text.secondary', fontSize: 14 }}>
          Maybe later
        </Button>
      )}
    </Box>
  );
}